import * as React from 'react';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import Autocomplete, { AutocompleteChangeReason } from '@mui/material/Autocomplete';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { IRecipientList } from '../../models/communication.types';
import { SyntheticEvent, useEffect, useMemo, useState, useRef } from 'react';
import { IPageInfo } from '../../models/page.types';
import { Chip } from '@material-ui/core';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

interface RecipientsListOrgzProps {
  recipients: IRecipientList[];
  onRecipientsChange: (options: IRecipientList[]) => void;
  onScroll: (endReached: boolean) => void;
  onInputChange: (value: string) => void;
  selectedRecipientsList: IRecipientList[];
  pageInfo: IPageInfo;
  allChecked: (value: boolean) => void;
  userAccountType: 'examiner' | 'administrator' | 'examcommitteeemployee';
}

export const RecipientsOrgzList: React.FC<RecipientsListOrgzProps> = (props) => {
  const [allChecked, setAllChecked] = useState<boolean>(false);
  const [dynamicMaxHeight, setDynamicMaxHeight] = useState<number>(200);
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const inputRootRef = useRef<HTMLDivElement>(null);
  const { allChecked: setAllCheckedCallback } = props;

  const limitTags = 8;
  const exceedsLimit = props.selectedRecipientsList.length > limitTags;
  const shouldHideBorder = exceedsLimit && isFocused;

  const getDisplayInfo = (option: IRecipientList): string => {
    return `(${option.email})`;
  };

  const getDisplayFunctionLabel = (option: IRecipientList): string => {
    if (option.isVerifier) {
      return '(W)';
    }

    if (option.accountType && option.accountType === 'examiner') {
      return '(E)';
    }

    return '';
  };

  useEffect(() => {
    setAllCheckedCallback(allChecked);
  }, [allChecked, setAllCheckedCallback]);

  useEffect(() => {
    const updateHeight = () => {
      if (inputRootRef.current) {
        const inputRoot = inputRootRef.current.querySelector('.MuiAutocomplete-inputRoot') as HTMLElement;
        if (inputRoot) {
          const chipsHeight = inputRoot.scrollHeight;
          const calculatedHeight = Math.min(Math.max(chipsHeight, 100), 200);
          setDynamicMaxHeight(calculatedHeight);
        }
      }
    };

    updateHeight();

    const timeoutId = setTimeout(updateHeight, 0);

    return () => clearTimeout(timeoutId);
  }, [props.selectedRecipientsList, allChecked]);

  const handleRecipientsChange = (
    event: SyntheticEvent<Element, Event>,
    value: IRecipientList[],
    reason: AutocompleteChangeReason,
  ) => {
    props.onRecipientsChange(value);
    setAllChecked(false);

    if (reason === 'clear') {
      setAllChecked(false);
    }
  };

  return (
    <Autocomplete
      ref={inputRootRef}
      sx={{
        '& .MuiOutlinedInput-root': {
          padding: 0,
        },
        '& .MuiAutocomplete-inputRoot': {
          flexWrap: 'wrap',
          padding: '8px',
          alignContent: 'flex-start',
        },
        '& .MuiAutocomplete-inputRoot.Mui-focused': {
          maxHeight: `${dynamicMaxHeight}px`,
          overflowY: 'auto',
        },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
          border: shouldHideBorder ? 'none' : undefined,
        },
        '& .MuiInputLabel-root.Mui-focused': {
          display: shouldHideBorder ? 'none' : undefined,
        },
      }}
      ListboxProps={{
        role: 'list-box',
        style: { maxHeight: '30vh' },
        onScroll: (event: React.SyntheticEvent) => {
          const listboxNode = event.currentTarget;
          if (Math.ceil(listboxNode.scrollTop + listboxNode.clientHeight) === Math.ceil(listboxNode.scrollHeight)) {
            props.onScroll(true);
          }
        },
      }}
      multiple
      limitTags={limitTags}
      id="recipients-checkbox-list"
      filterOptions={(x) => x}
      onInputChange={(e, value) => props.onInputChange(value)}
      options={props.recipients}
      value={props.selectedRecipientsList}
      disableCloseOnSelect
      getOptionLabel={(option) =>
        ` ${getDisplayFunctionLabel(option)} ${option.firstName} ${option.lastName} ${getDisplayInfo(option)}`
      }
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <React.Fragment key={option.email || option.userId || index}>
            <Chip
              label={`${option.firstName} ${option.lastName}`}
              {...getTagProps({ index })}
              style={{ color: '#2C2D3E', background: '#FAFAFF', border: '1px solid rgba(44, 45, 62, 0.3' }}
            />
          </React.Fragment>
        ))
      }
      renderOption={(props, option, { selected }) => (
        <li {...props}>
          <Checkbox disableRipple icon={icon} checkedIcon={checkedIcon} style={{ marginRight: 8 }} checked={selected} />
          {getDisplayFunctionLabel(option)} {option.firstName} {option.lastName} {getDisplayInfo(option)}
        </li>
      )}
      renderInput={(params) => (
        <TextField {...params} label="Wybierz" onFocus={() => setIsFocused(true)} onBlur={() => setIsFocused(false)} />
      )}
      onChange={handleRecipientsChange}
    />
  );
};
